import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetchJson, newIdempotencyKey } from "../apiClient";
import { CheckRow } from "./CapabilityCards";
import AgentLifecyclePanel from "./AgentLifecyclePanel";
import SharingPanel from "./SharingPanel";
import ToolPicker from "./ToolPicker";
import "./AdminCollections.css";

// Steps/seconds start empty and default to the server's caps once /limits arrives.
const EMPTY_OPTIONS = { model_profile: "", max_tokens: null, max_cost: null, spawn_subagents: false, licence: "" };
const EMPTY = { name: "", description: "", instructions: "", tools: [], max_steps: null, max_seconds: null,
  visibility: "private", options: EMPTY_OPTIONS };

const withDatabase = (path, database) =>
  database ? `${path}${path.includes("?") ? "&" : "?"}database=${encodeURIComponent(database)}` : path;

// Fields that have an error slot in the form; an error for any other field is shown form-level.
const SLOTTED_FIELDS = ["name", "description", "instructions", "visibility", "max_steps", "max_seconds", "tools",
  "options.model_profile", "options.max_tokens", "options.max_cost", "options.spawn_subagents", "options.licence"];
// Slotted fields inside the collapsed "Advanced settings" section (opened when one of them has an error).
const ADVANCED_FIELDS = ["visibility", "max_steps", "max_seconds", "options.model_profile", "options.max_tokens",
  "options.max_cost", "options.licence"];
const STATUS_VERSION_REQUIRED = 428;
const STATUS_CONFLICT = 409;

const sameValue = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/** Only the fields the user changed: an edit must never overwrite what it did not touch
 * (e.g. instructions the server withheld from the loaded view). */
const changedFields = (form, baseline) =>
  Object.fromEntries(Object.entries(form).filter(([key, value]) => !sameValue(value, baseline[key])));

const fromAgent = (agent) => ({
  name: agent.name, description: agent.description || "", instructions: agent.instructions || "",
  tools: [...agent.tools], max_steps: agent.max_steps, max_seconds: agent.max_seconds, visibility: agent.visibility,
  options: { ...EMPTY_OPTIONS, ...(agent.options || {}) },
});

/** The options object the API expects: unset budgets, the default profile and off switches are left out. */
const toOptions = (options) => Object.fromEntries(
  Object.entries(options || {}).filter(([, value]) => value !== null && value !== "" && value !== false
    && !(Array.isArray(value) && value.length === 0)));

const toBody = (fields) => ("options" in fields ? { ...fields, options: toOptions(fields.options) } : fields);

/** Create or edit an agent. Create sends an Idempotency-Key (one per editor session, so a
 * retried click cannot create two agents); edit sends If-Match with the version loaded, and a
 * 409 offers to reload the newer version instead of overwriting it. */
export default function AgentEditor({ database, agent, onDone }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(agent ? fromAgent(agent) : EMPTY);
  const [version, setVersion] = useState(agent ? agent.version : null);
  const [baseline, setBaseline] = useState(agent ? fromAgent(agent) : EMPTY);
  const [limits, setLimits] = useState(null);
  const [catalogue, setCatalogue] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [fieldError, setFieldError] = useState({});
  const [problems, setProblems] = useState({});
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(false);
  const [busy, setBusy] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [idempotencyKey] = useState(newIdempotencyKey);
  const mounted = useRef(true);
  const saving = useRef(false);

  // Set true on mount too: StrictMode runs mount -> cleanup -> mount.
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    Promise.all([apiFetchJson("/api/v1/agents/limits"), apiFetchJson("/api/v1/agents/tool-catalogue")])
      .then(([l, c]) => {
        if (!mounted.current) return;
        setLimits(l);
        setCatalogue(c.tools || []);
        setLoaded(true);
        if (!agent) setForm((f) => ({ ...f, max_steps: f.max_steps ?? l.max_steps,
          max_seconds: f.max_seconds ?? l.max_seconds }));
      })
      .catch((e) => { if (mounted.current) setError(e.message); });
    // Optional: without profiles (or the endpoint) the agent simply uses the default model.
    apiFetchJson("/api/v1/agents/model-profiles")
      .then((p) => { if (mounted.current) setProfiles((p.items || []).filter((x) => x.enabled)); })
      .catch(() => { if (mounted.current) setProfiles([]); });
  }, [agent]);

  // An empty number input stays empty (null) so the server's validation message shows, never 0.
  const parse = (e) => (e.target.type !== "number" ? e.target.value
    : e.target.value === "" ? null : Number(e.target.value));
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: parse(e) }));
  const setOption = (key) => (e) => {
    const value = parse(e);
    setForm((f) => ({ ...f, options: { ...f.options, [key]: value } }));
  };

  const reload = async () => {
    try {
      const latest = await apiFetchJson(`/api/v1/agents/${agent.id}`);
      if (!mounted.current) return;
      setForm(fromAgent(latest));
      setBaseline(fromAgent(latest));
      setVersion(latest.version);
      setConflict(false);
      setError("");
    } catch (e) {
      if (mounted.current) setError(e.message);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (saving.current) return;   // a double-click must not submit twice
    saving.current = true;
    setBusy(true);
    setFieldError({});
    setProblems({});
    setError("");
    try {
      if (agent) {
        await apiFetchJson(`/api/v1/agents/${agent.id}`, {
          method: "PATCH", headers: { "If-Match": String(version) },
          body: JSON.stringify(toBody(changedFields(form, baseline))) });
      } else {
        await apiFetchJson(withDatabase("/api/v1/agents", database), {
          method: "POST", headers: { "Idempotency-Key": idempotencyKey }, body: JSON.stringify(toBody(form)) });
      }
      onDone();
    } catch (err) {
      const detail = err.body && err.body.detail;
      const message = (detail && detail.message) || err.message;
      if (err.status === STATUS_CONFLICT && detail && detail.current_version) setConflict(true);
      else if (err.status === STATUS_VERSION_REQUIRED) setError(t("agents.versionRequired"));
      else if (detail && detail.invalid) setProblems(detail.invalid);
      else if (detail && SLOTTED_FIELDS.includes(detail.field)) {
        setFieldError({ [detail.field]: message });
        if (ADVANCED_FIELDS.includes(detail.field)) setAdvancedOpen(true);
      }
      else setError(message);
    } finally {
      saving.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const unchanged = Boolean(agent) && Object.keys(changedFields(form, baseline)).length === 0;

  const errorId = (field) => `agent-${field}-error`;
  const a11y = (field) => (fieldError[field]
    ? { "aria-invalid": true, "aria-describedby": errorId(field) } : {});
  const errorSlot = (field) => fieldError[field] && (
    <span id={errorId(field)} className="ds-help" role="alert">{fieldError[field]}</span>);

  const counter = (value, max) => max && <span className="ds-help">{t("agents.charCount", { count: value.length, max })}</span>;

  const canManage = !agent || agent.can_manage;
  return (
    <>
    <form className="ds-page ac-form-stack" onSubmit={save} aria-label={t(agent ? "agents.editorEditTitle" : "agents.editorCreateTitle")}>
      <header className="ds-page-header">
        <h2 className="ds-page-title">{t(agent ? "agents.editorEditTitle" : "agents.editorCreateTitle")}</h2>
      </header>
      {error && <div className="ds-alert ds-alert--danger" role="alert">{error}</div>}
      {conflict && (
        <div className="ds-alert ds-alert--warning" role="alert">
          {t("agents.conflict")}{" "}
          <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm" onClick={reload}>{t("agents.reload")}</button>
        </div>
      )}
      <div className="ds-field">
        <label className="ds-label" htmlFor="agent-name">{t("agents.fieldName")}</label>
        <input id="agent-name" {...a11y("name")} className="ds-input" value={form.name} onChange={set("name")}
               maxLength={limits?.name_max_chars} />
        {errorSlot("name")}
      </div>
      <div className="ds-field">
        <label className="ds-label" htmlFor="agent-description">{t("agents.fieldDescription")}</label>
        <input id="agent-description" {...a11y("description")} className="ds-input" value={form.description} onChange={set("description")}
               maxLength={limits?.description_max_chars} />
        {errorSlot("description")}
      </div>
      <div className="ds-field">
        <label className="ds-label" htmlFor="agent-instructions">{t("agents.fieldInstructions")}</label>
        <textarea id="agent-instructions" {...a11y("instructions")} className="ds-textarea" rows={8} value={form.instructions}
                  onChange={set("instructions")} maxLength={limits?.instructions_max_chars} />
        <span className="ds-help">{t("agents.instructionsHelp")}</span>
        {counter(form.instructions, limits?.instructions_max_chars)}
        {errorSlot("instructions")}
      </div>
      <ToolPicker catalogue={catalogue} loaded={loaded} selected={form.tools} problems={problems}
                  onChange={(tools) => setForm((f) => ({ ...f, tools }))} />
      {errorSlot("tools")}
      <CheckRow id="agent-spawn-subagents" checked={!!form.options.spawn_subagents}
                onChange={(e) => setForm((f) => ({ ...f, options: { ...f.options, spawn_subagents: e.target.checked } }))}
                label={t("agents.fieldSpawnSubagents")} help={t("agents.spawnSubagentsHelp")} />
      {errorSlot("options.spawn_subagents")}
      {/* Everything below has a working default: it stays out of the way unless the user opens it
          (or the server reports a problem with one of these fields). */}
      <details className="ac-form-stack" open={advancedOpen}
               onToggle={(e) => setAdvancedOpen(e.currentTarget.open)}>
      <summary className="ds-label">{t("agents.advancedSettings")}</summary>
      <div className="ds-form-row">
        <div className="ds-field">
          <label className="ds-label" htmlFor="agent-visibility">{t("agents.fieldVisibility")}</label>
          <select id="agent-visibility" {...a11y("visibility")} className="ds-select" value={form.visibility} onChange={set("visibility")}>
            <option value="private">{t("agents.visibilityPrivate")}</option>
            <option value="workspace">{t("agents.visibilityWorkspace")}</option>
          </select>
          {errorSlot("visibility")}
        </div>
        <div className="ds-field">
          <label className="ds-label" htmlFor="agent-steps">{t("agents.fieldMaxSteps")}</label>
          <input id="agent-steps" {...a11y("max_steps")} type="number" className="ds-input ds-input--sm" min={1} max={limits?.max_steps}
                 value={form.max_steps ?? ""} onChange={set("max_steps")} />
          {errorSlot("max_steps")}
        </div>
        <div className="ds-field">
          <label className="ds-label" htmlFor="agent-seconds">{t("agents.fieldMaxSeconds")}</label>
          <input id="agent-seconds" {...a11y("max_seconds")} type="number" className="ds-input ds-input--sm" min={1} max={limits?.max_seconds}
                 value={form.max_seconds ?? ""} onChange={set("max_seconds")} />
          {errorSlot("max_seconds")}
        </div>
      </div>
      <div className="ds-form-row">
        {profiles.length > 0 && (
          <div className="ds-field">
            <label className="ds-label" htmlFor="agent-model-profile">{t("agents.fieldModelProfile")}</label>
            <select id="agent-model-profile" {...a11y("options.model_profile")} className="ds-select"
                    value={form.options.model_profile || ""} onChange={setOption("model_profile")}>
              <option value="">{t("agents.modelProfileDefault")}</option>
              {profiles.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
            {errorSlot("options.model_profile")}
          </div>
        )}
        <div className="ds-field">
          <label className="ds-label" htmlFor="agent-max-tokens">{t("agents.fieldMaxTokens")}</label>
          <input id="agent-max-tokens" {...a11y("options.max_tokens")} type="number" className="ds-input ds-input--sm"
                 min={0} max={limits?.max_tokens || undefined} placeholder={t("agents.budgetUnlimited")}
                 value={form.options.max_tokens ?? ""} onChange={setOption("max_tokens")} />
          {errorSlot("options.max_tokens")}
        </div>
        <div className="ds-field">
          <label className="ds-label" htmlFor="agent-max-cost">{t("agents.fieldMaxCost")}</label>
          <input id="agent-max-cost" {...a11y("options.max_cost")} type="number" step="0.01" className="ds-input ds-input--sm"
                 min={0} max={limits?.max_cost || undefined} placeholder={t("agents.budgetUnlimited")}
                 value={form.options.max_cost ?? ""} onChange={setOption("max_cost")} />
          {errorSlot("options.max_cost")}
        </div>
      </div>
      <span className="ds-help">{t("agents.budgetHelp")}</span>
      <div className="ds-field">
        <label className="ds-label" htmlFor="agent-licence">{t("agents.fieldLicence")}</label>
        <input id="agent-licence" {...a11y("options.licence")} className="ds-input ds-input--sm"
               disabled={!canManage} value={form.options.licence || ""} onChange={setOption("licence")} />
        <span className="ds-help">{t("agents.licenceHelp")}</span>
        {errorSlot("options.licence")}
      </div>
      </details>
      <div className="ac-actions">
        <button type="button" className="ds-btn ds-btn--secondary" onClick={onDone}>{t("agents.back")}</button>
        <button type="submit" className="ds-btn ds-btn--primary" disabled={busy || unchanged}>{t("agents.save")}</button>
      </div>
    </form>
    {agent && agent.can_manage && <SharingPanel accessPath={`/api/v1/agents/${agent.id}/access`} />}
    {agent && agent.can_edit && <AgentLifecyclePanel agent={agent} />}
    </>
  );
}
